import {
  circleIntersections,
  rayCircleIntersection,
  pointAlongRay,
  ang2rad,
  rad2ang,
  deg2rad,
  rad2deg,
  vec_magnitude,
  vec_toAngle,
} from "./layout_geometry.js";

import { mapToRange } from '@/functions/utility.js'

let centerOffset = 0
let angleOffset = 0;

function makeHelpers({
  // this much space will be left between circles
  padding = 0,

  // when something goes wrong, we place a room here
  fallbackPoint = [0, 0],
} = {}) {
  /*
    NOTE: all access to `roomData` elements is via these `HELPERS`.
    Their internals can be updated if `roomData` schema changes.
   */
  const HELPERS = {
    getNodeType,
    getRadius,
    getAngle,
    _setAngle,
    setCenter,
    getCenter,
    setData,
    getData,
    delData,
  };

  function getNodeType(d) {
    // expect to return one of: `['hub', 'lounge', 'create_space', 'hybrid', 'digital']`
    return d.layoutType;
  }

  function getRadius(d) {
    // for layout, we pack nodes tightly, as simple circles;
    // by adding half the padding to each here, the visual distance
    // between any two circles will add up to `padding` as desired.
    return d.radius + padding / 2;
  }

  function getAngle(d) {
    // layout rules involve an angle that serves a "ray" from the origin
    return ang2rad(d.baseAngle);
  }
  function _setAngle(d, x) {
    // NOTE: this is only called on `candidateNode` copies;
    //       the angles of the original nodes are left alone.
    d.baseAngle = rad2ang(x);
  }

  function setCenter(d, pt) {
    // this helper determines how the positions are set on each node
    let [x, y] = pt || fallbackPoint;
    Object.assign(d, { x, y });
  }
  function getCenter(d) {
    // NOTE: sometimes the algorithm needs to read *back* positions it has set,
    //       so keep this in sync with the main `setCenter` helper above.
    let { x, y } = d;
    return [x, y];
  }

  let dataKey = Symbol("Layout algorithm temporary/internal data");
  function setData(d, v) {
    d[dataKey] = v;
  }
  function getData(d) {
    return d[dataKey];
  }
  function delData(d) {
    delete d[dataKey];
  }

  return HELPERS;
}

function arrangeCoreNodes(
  { getRadius, getAngle, setCenter, getCenter },
  hubNode,
  loungeNode,
  createNode
) {
  // fix the hub and lounge nodes around origin
  [hubNode, loungeNode].forEach(function (node) {
    let rayPoint = pointAlongRay(getAngle(node), getRadius(node))
    let hubRad = hubNode.radius
    let loungeRad = loungeNode.radius
    let ratio = 0
    if(hubRad > loungeRad) {
      ratio = loungeRad/hubRad
      let radOff = 1-ratio
      centerOffset = radOff*hubRad
      rayPoint[0]+=centerOffset
      rayPoint[1]-=centerOffset
    } else if(hubRad <= loungeRad) {
      ratio = hubRad / loungeRad
      let radOff = 1-ratio
      centerOffset = radOff*loungeRad
      rayPoint[0]-=centerOffset
      rayPoint[1]+=centerOffset
    }
    //(node, [x,y]), add to x,y depending on ratio between hub and lounge?
    setCenter(node, rayPoint);
  });

  // position the create ("new space") node
  /*
    NOTE: this node belongs "an equal distance from the HUB and the LOUNGE" (in top-right quadrant)
    i.e. it will be `createNode.radius + padding` away from _both_ `hubNode` and `loungeNode` simultaneously

    So we take a circle around `hubNode` x/y with radius of (in raw terms, i.e. inlining `getRadius` helper):

        hubNode.radius + padding + createNode.radius

    …and a similar circle around the center of `loungeNode`, and find their (should be two) intersections.
    We take the one to the left of a ray from the hub to the lounge, which is the first intersection returned.
    */
  let pts = circleIntersections(
    getCenter(hubNode),
    getRadius(hubNode) + getRadius(createNode),
    getCenter(loungeNode),
    getRadius(loungeNode) + getRadius(createNode)
  );
  if (pts.length) {
    let pos = pts[0]
    setCenter(createNode, pos);
  } else {
    // NOTE: this means the hub and lounge nodes are too far apart from each other, somehow!
    console.warn(
      "Hub and lounge nodes are poorly arranged, can't place create node!"
    );
    setCenter(createNode, null);
  }
}

function arrangeNodeAtFixedAngle(
  { getRadius, setCenter, getCenter },
  node,
  placedNodes
) {
  // console.log('NODE: ', node.topic)
  // find a place for `node` avoiding conflicts with already `placedNodes`,
  // not allowing any deviation from the angle configured for this node.

  /*
    NOTE: this works by intersecting the incoming node's permitted "ray" with
    an extended-size version of every existing circle. The farthest such intersection
    is the closest to the origin that we can place the new node without causing overlap.
  */
  let ogAngle = Number(node.baseAngle)
  // let angles = [ogAngle, offsetAngle(ogAngle-angleOffset), offsetAngle(ogAngle+angleOffset), offsetAngle(ogAngle-angleOffset*2), offsetAngle(ogAngle+angleOffset*2)]
  let angles = [ogAngle]
  let angleData = []
  let angleItem = {
    intersections: [],
    intersectionAmounts: 0,
    furthestDist: 0,
    furthestLoc: false,
  }
  angles.forEach(() => angleData.push({...angleItem}))
  // console.log(angles)

  angles.forEach((angle,index) => {
    let farthestLoc, _cachedD = 0;
    // let closest = []
    let nodeAngle = ang2rad(angle)
    let intersections = []
    let intersectionAmounts = 0

    placedNodes.forEach(function (placedNode) {
      let placedCenter = getCenter(placedNode)
      let placedRad = getRadius(placedNode)
      let totRad = placedRad + getRadius(node)

      // check if placedNode overlays center (crosses all quadrants) (must overlap more than just radius to be fully over center)
      let overlapsCenter = false
      let overlapsX = false
      let overlapsY = false
      if(placedCenter[0] >= 0) {
        if((placedCenter[0] - placedRad) < (0 - placedRad/3)) overlapsX = true
      } else {
        if((placedCenter[0] + placedRad) > (0 + placedRad/3)) overlapsX = true
      }
      if(placedCenter[1] >= 0) {
        if((placedCenter[1] - placedRad) < (0 - placedRad/3)) overlapsY = true
      } else {
        if((placedCenter[1] + placedRad) > (0 + placedRad/3)) overlapsY = true
      }
      if(overlapsX && overlapsY) overlapsCenter = true

      let {loc = 0, d = 0} = rayCircleIntersection(
        nodeAngle,
        placedCenter,
        totRad,
        overlapsCenter
      );
      if (!loc) return;
      // if(!overlapsCenter) { // only works if can identify which side mostly overlaps on
        intersections.push(placedNode)
        intersectionAmounts+=d //larger d = further away.. (i think)
      // }

      let dist = vec_magnitude(loc); //calc distance to this node
      if (dist > _cachedD) { //is distance greater than any of the previous furthest locations
        farthestLoc = loc;
        _cachedD = dist;
      }
    });
    // console.log(angle-ogAngle,index, '\nno intersections:',intersections.length, '\nint amounts:',intersectionAmounts)
    angleData[index].intersections = intersections
    angleData[index].intersectionAmounts = intersectionAmounts
    angleData[index].furthestDist = _cachedD
    angleData[index].furthestLoc = farthestLoc
  });

  let angleIndex = 0;

  // console.log('BEST ANGLE: ', angleIndex, ogAngle-angles[angleIndex])
  let farthestLoc = angleData[angleIndex].furthestLoc
  if(farthestLoc) {
    setCenter(node, farthestLoc);
  }

  else {
    if (placedNodes.length) {
      console.warn(
        "Node didn't intersect any previously-placed nodes!",
        node,
        placedNodes
      );
    }
    setCenter(node, null);
  }
}

function arrangeNodeWithinQuadrant(HELPERS, node, placedNodes, segments) {
  let { setCenter, getCenter, setData, getData } = HELPERS;
  /*
  This algorithm allows more flexible placement of nodes,
  within the whole quadrant surrounding `node.baseAngle`.

  It works by trying to place at the angles between any
  already `placedNodes` whose centers fall within the same
  quadrant. The placement closest to the origin is chosen.
  */
  if(placedNodes.length < 15) {
    segments = 70
  }

  let QUAD_ANGLE = (Math.PI * 2) / segments
  let posAngle = deg2rad(node.baseAngle)
  // the starting/ending angles of the quadrant:
  let minA = posAngle - (posAngle % QUAD_ANGLE)
  let maxA = minA + QUAD_ANGLE;

  // first we pre-calculate (and memoize for future calls!)
  // and sort by the angle at which each existing node lies.
  let sortedInfo = placedNodes
    .map(function (placedNode) {
      let d = getData(placedNode);
      if (!d) {
        let pt = getCenter(placedNode),
          angle = vec_toAngle(pt);
          //remap from 0 -> PI and 0 -> -PI to 0 -> 2PI
          let remapAng = angle
          if(remapAng >= 0) {
            remapAng = mapToRange(remapAng, 0, Math.PI, Math.PI*2, Math.PI)
          } else {
            remapAng = Math.abs(remapAng)
          }
          remapAng+=(Math.PI/2)
          if(remapAng > (Math.PI * 2)) {
            remapAng -= Math.PI*2
          }
        // NOTE: side effect!
        setData(placedNode, (d = { angle: remapAng, topic: placedNode.topic }));
      }
      return d;
    })
    .sort(function (a, b) {
      return a.angle - b.angle;
    });

  // find the first existing node within this quadrant, as well as
  // the index of the first existing node *outside* of the quadrant.
  let minIdx = sortedInfo.findIndex(({ angle }) => angle >= minA),
    maxIdx = sortedInfo.findIndex(({ angle }) => angle > maxA);
  if (maxIdx === -1) {
    // nothing found after this quadrant's ending
    maxIdx = sortedInfo.length; // set past end of array
  }
  if (minIdx === -1) {
    // nothing found after this quadrant's beginning
    minIdx = maxIdx; // (`anglesToTry` just one midway of quadrant itself)
  }

  // now for each node whose center falls within the quadrant
  // [other nodes may spill *into* this quadrant; handled later…]
  // add an angle on each side of the existing node to try use
  // while placing the incoming node.
  /*
    NOTE: the idea is that the _center_ of each existing `placedNodes`
    is naturally the *worst* place to put the incoming one. To a certain
    point, the farther away from the "bulges" of existing circles,
    the better. Thus, the midpoints _between_ (furthest from) existing pairs
    are possibly the *best* places to check for a good fit towards the origin.
  */
  let anglesToTry = [],
    prevAngle = minA,
    prevprevAngle = null;
  const midpoint = (a1, a2) => (a1 + a2) / 2;
  //add fixed/original angle
  anglesToTry.push(posAngle);
  //add min and max angles of segment
  anglesToTry.push(posAngle-(QUAD_ANGLE/2));
  anglesToTry.push(posAngle+(QUAD_ANGLE/2));
  //midpoints between spaces within same segment
  for (let i = minIdx; i < maxIdx; i += 1) {
    let { angle } = sortedInfo[i];
    anglesToTry.push(midpoint(prevAngle, angle));
    if(prevprevAngle) {
      anglesToTry.push(midpoint(prevprevAngle, angle));
    }
    prevprevAngle = prevAngle
    prevAngle = angle;
  }
  anglesToTry.push(midpoint(prevAngle, maxA));
  if(prevprevAngle) anglesToTry.push(midpoint(prevprevAngle, maxA));
  //console.log([minA, ...anglesToTry, maxA].map(rad2ang));

  // run the fixed-angle method once for each angle to try
  let candidates = anglesToTry
    .map(function (a) {
      let candidateNode = Object.create(node); // new copy, inheriting original properties
      // _setAngle(candidateNode, a);
      candidateNode.baseAngle = rad2deg(a)
      arrangeNodeAtFixedAngle(HELPERS, candidateNode, placedNodes);
      candidateNode._dist = vec_magnitude(getCenter(candidateNode));
      return candidateNode;
    })
    .sort(function (a, b) {
      return a._dist - b._dist;
    });
  // and (after sorting) use the position landing closest to the origin
  //console.log("using", rad2ang(getAngle(candidates[0])));
  setCenter(node, getCenter(candidates[0]));
}

export function arrangeNewNode(placedNodes) {
  const opts = { padding: 5 }
  const HELPERS = makeHelpers(opts);
  let { getCenter, setData, getData } = HELPERS;
  /*
  This algorithm allows more flexible placement of nodes,
  within the whole quadrant surrounding `node.baseAngle`.

  It works by trying to place at the angles between any
  already `placedNodes` whose centers fall within the same
  quadrant. The placement closest to the origin is chosen.
  */
  let QUAD_ANGLE = Math.PI * 2 //segment is full circle to find best pos anywhere
  // the starting/ending angles of the quadrant:
  let minA = 0;
  let maxA = minA + QUAD_ANGLE;

  // first we pre-calculate (and memoize for future calls!)
  // and sort by the angle at which each existing node lies.
  let sortedInfo = placedNodes
    .map(function (placedNode) {
      let d = getData(placedNode);
      if (!d) {
        // let rayPoint = pointAlongRay(getAngle(placedNode), getRadius(placedNode))
        // setCenter(placedNode, rayPoint)
        // console.log(rayPoint)
        let pt = getCenter(placedNode),
          angle = vec_toAngle(pt);

          //remap from 0 -> PI and 0 -> -PI to 0 -> 2PI
          let remapAng = angle
          if(remapAng >= 0) {
            remapAng = mapToRange(remapAng, 0, Math.PI, Math.PI*2, Math.PI)
          } else {
            remapAng = Math.abs(remapAng)
          }
          remapAng+=(Math.PI/2)
          if(remapAng > (Math.PI * 2)) remapAng -= Math.PI*2
        // NOTE: side effect!
        setData(placedNode, (d = { angle: remapAng, topic: placedNode.topic, radius: placedNode.radius, x: placedNode.x, y: placedNode.y }));
      }
      return d;
    })
    .sort(function (a, b) {
      return a.angle - b.angle;
    });

  // find the first existing node within this quadrant, as well as
  // the index of the first existing node *outside* of the quadrant.
  let minIdx = sortedInfo.findIndex(({ angle }) => angle >= minA),
    maxIdx = sortedInfo.findIndex(({ angle }) => angle > maxA);
  if (maxIdx === -1) {
    // nothing found after this quadrant's ending
    maxIdx = sortedInfo.length; // set past end of array
  }
  if (minIdx === -1) {
    // nothing found after this quadrant's beginning
    minIdx = maxIdx; // (`anglesToTry` just one midway of quadrant itself)
  }

  // now for each node whose center falls within the quadrant
  // [other nodes may spill *into* this quadrant; handled later…]
  // add an angle on each side of the existing node to try use
  // while placing the incoming node.
  /*
    NOTE: the idea is that the _center_ of each existing `placedNodes`
    is naturally the *worst* place to put the incoming one. To a certain
    point, the farther away from the "bulges" of existing circles,
    the better. Thus, the midpoints _between_ (furthest from) existing pairs
    are possibly the *best* places to check for a good fit towards the origin.
  */
  let biggestGap = 0
  let biggestGapIndex = 0

  let anglesToTry = [],
    prevAngle = minA,
    prevprevAngle = null;
  const midpoint = (a1, a2) => (a1 + a2) / 2;
  for (let i = minIdx; i < maxIdx; i += 1) {
    let { angle } = sortedInfo[i];
    let diff = angle - prevAngle
    if(diff > biggestGap) {
      biggestGap = diff
      biggestGapIndex = i
    }

    anglesToTry.push(midpoint(prevAngle, angle));
    if(prevprevAngle) {
      anglesToTry.push(midpoint(prevprevAngle, angle));
    }
    prevprevAngle = prevAngle
    prevAngle = angle;
  }
  let diff = maxA - prevAngle
  if(diff > biggestGap) {
    biggestGap = diff
    biggestGapIndex = sortedInfo.length
  }
  anglesToTry.push(midpoint(prevAngle, maxA));
  if(prevprevAngle) anglesToTry.push(midpoint(prevprevAngle, maxA));
  //console.log([minA, ...anglesToTry, maxA].map(rad2ang));

  if(placedNodes.length < 15) { //prevent the circles becoming a square around the core nodes at the start
    let newAngle = 0
    if(biggestGapIndex === sortedInfo.length) {
      newAngle = midpoint(sortedInfo[sortedInfo.length-1].angle, maxA)
    } else {
      if(biggestGapIndex - 1 > 0) {
        newAngle = midpoint(sortedInfo[biggestGapIndex-1].angle, sortedInfo[biggestGapIndex].angle)
      } else {
        newAngle = midpoint(minA, sortedInfo[biggestGapIndex].angle)
      }
    }
    return { baseAngle: rad2deg(newAngle) }

  } else {
    // run the fixed-angle method once for each angle to try
    let candidates = anglesToTry
      .map(function (a) {
        let candidateNode = Object.create({
          baseAngle: 178,
          layoutType: "digital",
          radius: 20,
          topic: "Test",
          x: 0,
          y: 0,
        }); // new copy, inheriting original properties
        candidateNode.baseAngle = rad2deg(a)
        arrangeNodeAtFixedAngle(HELPERS, candidateNode, placedNodes);
        candidateNode._dist = vec_magnitude(getCenter(candidateNode)); //get dist from center
        return candidateNode;
      })
      .sort(function (a, b) { //ascending order
        return a._dist - b._dist;
      });

    return candidates[0]
  }
}

/*
  This takes in an array of `roomData` which contains objects of the form:

      {radius, baseAngle, layoutType}

  One should have `layoutType` of `'hub'`, one of `'lounge'`, and the "New Space" button
  a `layoutType` of `'create_space'`; the others should be of type `'hybrid'` or `'digital'`.
  All must have a `radius`, and all but the `'create_space'` button should have a `baseAngle`.

  Each object will be assigned (in-place) two additional `{x,y}` properties for its layout.
*/
export function layoutRooms(roomData, opts, angleOff) {
  const HELPERS = makeHelpers(opts);

  const { getNodeType, delData } = HELPERS;

  angleOffset = Number(angleOff)

  let createNode, loungeNode, hubNode;
  let otherNodes = roomData.filter(function (node) {
    let nodeType = getNodeType(node);
    if (nodeType === "create_space") createNode = node;
    else if (nodeType === "lounge") loungeNode = node;
    else if (nodeType === "hub") hubNode = node;
    // NOTE: implicit false-y return for the "core nodes" above
    else return true; // all the rest are `otherNodes`
  });

  let coreNodes = [hubNode, loungeNode, createNode].filter(Boolean);
  if (coreNodes.length === 3) {
    arrangeCoreNodes(HELPERS, ...coreNodes);
  } else {
    console.warn(
      "Not all core nodes were found in provided data!",
      "Only found",
      coreNodes.map((d) => d.layoutType).join("+") || "[none]",
      "within",
      roomData
    );
    coreNodes.forEach((node) => {
      HELPERS.setCenter(node, null);
    });
  }

  let placedNodes = coreNodes.slice(0);
  if (otherNodes.length > 250) {
    // NOTE: we compare each node to all previously placed nodes; this scales approx. O(N^2)
    // consider wang2006 front-chain concept or other e.g. quadtree optimization if necessary
    // [some interim optimizations of e.g. `rayCircleIntersection` calls could be done as well]
    console.warn(
      `There are ${otherNodes.length} other nodes to place! Layout may be slow to compute.`
    );
  }
  otherNodes.forEach(function (node) {
    let nodeType = getNodeType(node);
    if (nodeType === "hybrid") arrangeNodeAtFixedAngle(HELPERS, node, placedNodes);
    else if (nodeType === "digital") arrangeNodeWithinQuadrant(HELPERS, node, placedNodes, angleOffset);
    else {
      console.warn(`Unknown ${nodeType} node:`, node);
      HELPERS.setCenter(node, null);
    }
    placedNodes.push(node);
  });
  placedNodes.forEach(delData);
}

/*
  This returns a bounding box for the calculated layout,
  suitable for passing into `makeCircleTransformer()` as
  its `srcBounds` (perhaps after fixing midX/midY to 0).
*/
export function determineRoomBounds(roomData, opts) {
  const { getRadius, getCenter } = makeHelpers(opts);

  let [minX, minY, maxX, maxY] = [0, 0, 0, 0];
  roomData.forEach(function expandBoundsToFit(node) {
    let [x, y] = getCenter(node),
      r = getRadius(node);
    minX = Math.min(minX, x - r);
    minY = Math.min(minY, y - r);
    maxX = Math.max(maxX, x + r);
    maxY = Math.max(maxY, y + r);
  });
  return { minX, minY, maxX, maxY };
}

/*
  This takes in two bounding boxes each of the form…

    {
      minX[, midX], maxX,
      minY[, midY], maxY,
    }

  and returns a `fn({x,y,r})` which in turn will return
  objects `{x,y,r}` after transforming those circles
  according to the `srcBounds` and `dstBounds` values.
  Unless `flipY` is false, the output `y` will also be
  inverted around the destination bounds' center.

  The point of all this is to take in circles laid out (in `srcBounds`)
  without regard for the currently available display size,
  and fit them into a target viewport (represented by `dstBounds`).

  The original center may be preserved (or a new center forced)
  by providing the `midX`/`midY` values of the respective parameters.
  (If any of these is not given, it is calculated from the min/max.)
*/
export function makeCircleTransformer(srcBounds, dstBounds, flipY = true, rev) {
  let srcMidX =
      "midX" in srcBounds
        ? srcBounds.midX
        : (srcBounds.minX + srcBounds.maxX) / 2,
    srcWidthA = srcMidX - srcBounds.minX,
    srcWidthB = srcBounds.maxX - srcMidX;
  let dstMidX =
      "midX" in dstBounds
        ? dstBounds.midX
        : (dstBounds.minX + dstBounds.maxX) / 2,
    dstWidthA = dstMidX - dstBounds.minX,
    dstWidthB = dstBounds.maxX - dstMidX;

  let srcMidY =
      "midY" in srcBounds
        ? srcBounds.midY
        : (srcBounds.minY + srcBounds.maxY) / 2,
    srcHeightA = srcMidY - srcBounds.minY,
    srcHeightB = srcBounds.maxY - srcMidY;
  let dstMidY =
      "midY" in dstBounds
        ? dstBounds.midY
        : (dstBounds.minY + dstBounds.maxY) / 2,
    dstHeightA = dstMidY - dstBounds.minY,
    dstHeightB = dstBounds.maxY - dstMidY;

  // NOTE: when offset, scale-to-fit varies on A vs. B side of centers
  let xScale = Math.min(dstWidthA / srcWidthA, dstWidthB / srcWidthB),
    yScale = Math.min(dstHeightA / srcHeightA, dstHeightB / srcHeightB),
    rScale = Math.min(xScale, yScale);
  // we don't want to stretch, so use same for all!
  xScale = yScale = rScale;
  if (flipY) yScale = -yScale;

  if(!rev) {
    return ({ x, y, r }) => {
      return {
        x: xScale * (x - srcMidX) + dstMidX,
        y: yScale * (y - srcMidY) + dstMidY,
        r: rScale * r,
      };
    };
  } else {
    return ({ x, y, r }) => {
      return {
        x: ((x + srcMidX) - dstMidX) / xScale,
        y: ((y + srcMidY) - dstMidY) / yScale,
        r: r / rScale,
      };
    };
  }
}
